import type { Ref } from 'vue'
import { onMounted, onUnmounted, unref, watch } from 'vue'
import type { MaybeRef } from '@vueuse/core'
import { useInfiniteScroll as useInfiniteScrollWhenScrolling } from '@vueuse/core'

interface Options {
  element: MaybeRef<HTMLElement | null>
  isLoading: Ref<boolean>
  isAllLoaded: Ref<boolean>
  isReady: Ref<boolean>
  scrollDistance?: number
  loadMore: () => void | Promise<void>
}

const SCROLL_DISTANCE = 150

const isReadyToLoadMore = (
  isLoading: MaybeRef<boolean>,
  isReady: MaybeRef<boolean>,
  isAllLoaded: MaybeRef<boolean>,
): boolean => !unref(isLoading) && unref(isReady) && !unref(isAllLoaded)

export default function ({
  element,
  loadMore,
  isLoading,
  isReady,
  isAllLoaded,
  scrollDistance = SCROLL_DISTANCE,
}: Options) {
  const hasElementScroll = (container: MaybeRef<HTMLElement>): boolean => {
    const { scrollHeight, clientHeight } = unref(container)
    return scrollHeight > clientHeight
  }

  useInfiniteScrollWhenScrolling(
    element,
    async () => {
      if (!isReadyToLoadMore(isLoading, isReady, isAllLoaded)) {
        return
      }

      await loadMore()
    },
    { distance: scrollDistance },
  )

  const fillUpList = () => {
    if (!unref(element) || hasElementScroll(element as HTMLElement)) return

    Promise.resolve(loadMore).catch((e) => {
      throw new Error(e)
    })
  }

  onMounted(() => {
    window.addEventListener('resize', fillUpList)

    watch(
      [isLoading],
      () => {
        if (!isReadyToLoadMore(isLoading, isReady, isAllLoaded)) {
          return
        }

        fillUpList()
      },
      { immediate: true, flush: 'post' },
    )
  })

  onUnmounted(() => {
    window.removeEventListener('resize', fillUpList)
  })
}
